import {UICorePlugin, type EventMap, type SupportedVersion} from '@clappr/core'
import type {Options} from 'common/types/options'
import {Events} from 'common/types/events'
import {AdvertisementBlockType} from 'common/api/records'
import type {Core} from 'common/components/core'
import type {MediaControl} from 'common/plugins/media-control'
import type {Suggest} from 'common/plugins/suggest'
import type {MultiPlayer} from 'common/plugins/multiplayer'
import type {
  Advertisement,
  AdvertisementEvent
} from 'common/plugins/advertisement'

export class Replay<T extends Options> extends UICorePlugin<T> {
  public static SHOW_TIMEOUT = 1000
  public static REPLAY_TIMEOUT = 7000

  public core!: Core<T>
  private showTimeout?: number
  private replayTimeout?: number

  public get name(): string {
    return 'replay'
  }

  public get supportedVersion(): SupportedVersion {
    return {min: '0.5.0'}
  }

  public get events(): EventMap {
    return {
      'click .eplayer-replay-button': 'playAgain',
      'click .eplayer-replay-canceled': 'cancelTimeout'
    }
  }

  private get mediaControl(): MediaControl<T> | null {
    return this.core.getPlugin('mediaControl') as MediaControl<T> | null
  }

  private get suggest(): Suggest<T> | null {
    return this.core.getPlugin('suggest') as Suggest<T> | null
  }

  private get multiPlayer(): MultiPlayer<T> | null {
    return this.core.getPlugin('multiplayer') as MultiPlayer<T> | null
  }

  private get advertisement(): Advertisement<T> | null {
    return this.core.getPlugin('advertisement') as Advertisement<T> | null
  }

  public bindEvents(): void {
    this.listenTo(
      this.core,
      Events.CORE_ACTIVE_CONTAINER_CHANGED,
      this.onActiveContainerChange
    )

    if (this.mediaControl) {
      this.listenTo(
        this.mediaControl,
        Events.MEDIACONTROL_RENDERED,
        this.render
      )
    }

    if (this.suggest) {
      this.listenTo(
        this.suggest,
        Events.SUGGEST_RENDERED,
        this.onSuggestRendered
      )
    }

    if (this.multiPlayer) {
      this.listenTo(this.multiPlayer, Events.MULTIPLAYER_RENDERED, this.disable)
    }

    if (this.advertisement) {
      this.listenTo(
        this.advertisement,
        Events.ADVERTISEMENT_LOADING,
        this.onAdLoading
      )
    }

    const {activeContainer} = this.core

    if (activeContainer) {
      this.listenTo(activeContainer, Events.CONTAINER_PLAY, this.cancelTimeout)
      this.listenTo(activeContainer, Events.CONTAINER_ENDED, this.startTimeout)
    }
  }

  private onActiveContainerChange(): void {
    this.stopListening()
    this.bindEvents()
  }

  private onAdLoading(event: AdvertisementEvent): void {
    const postRoll = event.blockType === AdvertisementBlockType.POST_ROLL

    if (!postRoll) {
      return
    }

    this.stopListening()
    this.listenToOnce(
      this.advertisement,
      Events.ADVERTISEMENT_CONTENT_RESUME_REQUESTED,
      () => {
        this.bindEvents()
        window.setTimeout(() => this.startTimeout(), 0)
      }
    )
  }

  private onSuggestRendered(): void {
    if (this.$el.is(':visible')) {
      this.$el.hide()
    } else {
      clearTimeout(this.showTimeout)
    }
  }

  public stopListening(): this {
    this.cancelTimeout()

    return super.stopListening()
  }

  private playAgain = (): void => {
    this.cancelTimeout()
    this.core.activeContainer?.play()
  }

  private startTimeout(): void {
    const {replayTimeout} = this.core.options

    if (replayTimeout === 0) {
      this.playAgain()

      return
    }

    this.showTimeout = window.setTimeout(
      () => this.$el.show(),
      Replay.SHOW_TIMEOUT
    )
    this.replayTimeout = window.setTimeout(
      this.playAgain,
      replayTimeout != null ? replayTimeout * 1e3 : Replay.REPLAY_TIMEOUT
    )
  }

  private cancelTimeout(): void {
    clearTimeout(this.showTimeout)
    clearTimeout(this.replayTimeout)
    this.$el.hide()
  }

  public render(): this {
    const $wrapper = this.mediaControl?.$el.find('.eplayer-replay-wrapper')

    if ($wrapper) {
      this.setElement($wrapper)
      this.$el.hide()
    }

    return this
  }
}
